import { gql } from "@apollo/client";
import { PERMISSION_TO_DISPLAY_NAME } from "@src/features/libraryItem/course/shareButton/constants";
import {
  useRemoveUserSharingPermissionMutation,
  useUpdateUserSharingPermissionMutation,
} from "@src/features/libraryItem/course/shareButton/SharingPermissionRow.generated";
import { useToast } from "@src/lib/hooks/useToast";
import { FC, useMemo } from "react";
import { GraphqlOperations, SharingPermission } from "@src/types.generated";
import Text from "@src/lib/ui/Text";
import { UserSharingPermissionFragment } from "@src/fragments.generated";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@src/lib/ui/DropdownMenu";
import { Button } from "@src/lib/ui/Button";
import ChevronDownIcon from "@src/lib/icons/12px/chevron-down";
import CheckmarkIcon from "@src/lib/icons/12px/checkmark-curved";

type Props = {
  courseId?: number;
  moduleId?: number;
  skillId?: number;
  trainingResourceId?: string;
  userSharingPermission: UserSharingPermissionFragment;
};

const SharingPermissionRow: FC<Props> = ({
  courseId,
  moduleId,
  skillId,
  trainingResourceId,
  userSharingPermission,
}) => {
  const { addToast, addErrorToast } = useToast();
  const user = userSharingPermission.user;
  const refetches = useMemo(() => {
    if (courseId) {
      return [GraphqlOperations.Query.CourseSharingPermissions];
    } else if (moduleId) {
      return [GraphqlOperations.Query.ModuleSharingPermissions];
    } else if (trainingResourceId) {
      return [GraphqlOperations.Query.TrainingResourceSharingPermissions];
    } else {
      return [GraphqlOperations.Query.ShareSkillDialog_Skill];
    }
  }, [courseId, moduleId, trainingResourceId]);
  const [updateUserSharingPermission] = useUpdateUserSharingPermissionMutation({
    refetchQueries: refetches,
    onCompleted: (data) => {
      if (data.updateUserSharingPermission.success) {
        addToast({
          message: `${user.name || "User"}'s permission updated`,
        });
      } else {
        addErrorToast({ ...data, callsite: "update-user-sharing-permission" });
      }
    },
  });
  const [removeUserSharingPermission] = useRemoveUserSharingPermissionMutation({
    variables: {
      input: {
        courseId,
        moduleId,
        skillId,
        trainingResourceId,
        userId: user.id,
      },
    },
    refetchQueries: refetches,
    onCompleted: (data) => {
      if (data.removeUserSharingPermission.success) {
        addToast({
          message: `${user.name || "User"} removed`,
        });
      } else {
        addErrorToast({ ...data, callsite: "remove-user-sharing-permission" });
      }
    },
  });

  const permissionOptions = useMemo(() => {
    const options = [SharingPermission.Edit];
    if (courseId) options.push(SharingPermission.Comment);
    if (moduleId || trainingResourceId)
      options.push(SharingPermission.ViewInLibrary);
    return options;
  }, [courseId, moduleId, trainingResourceId]);

  return (
    <div className="flex items-center justify-between rounded-xl">
      <Text type="P2" fontWeight="Medium" className="text-gray-8">
        {user.name}
      </Text>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button variant="ghost" size="sm" className="gap-1">
            <Text type="P2" fontWeight="SemiBold">
              {PERMISSION_TO_DISPLAY_NAME[userSharingPermission.permission]}
            </Text>
            <ChevronDownIcon className="size-3 opacity-50" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end">
          {permissionOptions.map((permission) => (
            <DropdownMenuItem
              key={permission}
              onClick={() =>
                updateUserSharingPermission({
                  variables: {
                    input: {
                      courseId,
                      moduleId,
                      skillId,
                      trainingResourceId,
                      userId: user.id,
                      permission,
                    },
                  },
                })
              }
            >
              <div className="flex w-full items-center gap-2">
                {userSharingPermission.permission === permission ? (
                  <CheckmarkIcon className="size-3 text-gray-8" />
                ) : (
                  <div className="size-3" />
                )}
                {PERMISSION_TO_DISPLAY_NAME[permission]}
              </div>
            </DropdownMenuItem>
          ))}
          <DropdownMenuSeparator />
          <DropdownMenuItem
            className="text-destructive focus:text-destructive"
            onClick={() => removeUserSharingPermission()}
          >
            <div className="flex w-full items-center gap-2">
              <div className="size-3" />
              Remove access
            </div>
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  );
};

export default SharingPermissionRow;

gql`
  mutation updateUserSharingPermission(
    $input: UpdateUserSharingPermissionInput!
  ) {
    updateUserSharingPermission(input: $input) {
      success
      error {
        code
        message
      }
      userSharingPermission {
        ...UserSharingPermission
      }
    }
  }

  mutation removeUserSharingPermission(
    $input: RemoveUserSharingPermissionInput!
  ) {
    removeUserSharingPermission(input: $input) {
      success
      error {
        code
        message
      }
    }
  }
`;
